/**
 * A leitura da Visão executiva: as cinco perguntas que um dono ou diretor faz, montadas
 * sobre os números que as outras telas já calculam.
 *
 * NENHUMA regra nova mora aqui. O risco sai de `opsRisco` (o mesmo helper do cartão Risco
 * financeiro da Carteira, espelho de `risco-financeiro.util.ts` na API), a ocupação sai da
 * grade do Mapa com as faixas de Configurações › Ocupação (`faixasOcupacao`), as horas
 * livres saem de `gradeCapacidade` e o prazo que vale numa OP sai de `entregaDaOp`. Esta
 * tela só agrupa e divide: por isso a soma por marca e a soma por célula fecham com o
 * total, e é isso que o spec ao lado prova.
 */
import { inicioDaSemana } from '@/app/mapa/helpers/formatMapa';
import { horasLivresRestantes } from '@/app/mapa/helpers/gradeCapacidade';
import { entregaDaOp } from '@/app/ops/helpers/opsFilters';
import { OpEmRisco, resumoDoRisco, riscoDaOp } from '@/app/ops/helpers/opsRisco';
import { ICalendarioFabrica, primeiroDiaUtilDepoisDe } from '@/domain/configuracao/helpers/calendarioFabrica';
import { faixaDaOcupacao, FaixaDeOcupacao, IFaixasDeOcupacao } from '@/domain/configuracao/helpers/faixasOcupacao';
import { fimDoExpedienteEmMinutos, horaDoMinuto } from '@/domain/configuracao/helpers/jornada';
import { IJornadaConfig } from '@/domain/configuracao/interfaces/configuracao';
import { GradeQuadro, GradeSemana, GradeSemanas } from '@/domain/mapa/interfaces/mapa';
import {
  atrasoDaOp,
  gravidadeDoFarol,
  ICortesDaGravidade,
  TGravidadeFarol,
} from '@/domain/ops/helpers/gravidadeDoFarol';
import { EnrichedOp, RiscoFinanceiro } from '@/domain/ops/interfaces/ops';

const PCT_CHEIO = 100;

/** Quantas marcas o bloco 5 mostra antes do "ver todas". */
export const MARCAS_VISIVEIS = 7;

/** O bloco 1: o risco da carteira com os dois denominadores que o explicam. */
export interface IRiscoDaVisao {
  /** O MESMO resumo do cartão Risco financeiro da Carteira. */
  resumo: RiscoFinanceiro;
  /** OPs da carteira: o "de M" de "N de M OPs não saem no prazo". */
  opsCarteira: number;
  /** Faturamento da carteira: o denominador do percentual. */
  faturamentoCarteira: number;
  /** Quanto do faturamento está em risco, em pontos percentuais; `null` sem faturamento. */
  pctFaturamento: number | null;
  /**
   * Quantas OPs VERMELHAS há em cada grau do atraso. A cor é a do motor e não muda aqui:
   * o grau só diz por onde começar quando quase toda a carteira está vermelha.
   */
  porGravidade: Readonly<Record<TGravidadeFarol, number>>;
}

/** Uma semana da mini grade de uma célula, já com a leitura que pinta o quadrinho. */
export interface ISemanaDaVisao {
  inicio: string;
  /** A semana que a fábrica está vivendo, como a grade do Mapa a marca. */
  atual: boolean;
  ocupacaoPct: number | null;
  /** Semana sem nenhuma hora programada: fica neutra, como o quadrinho do Mapa. */
  semCarga: boolean;
}

/**
 * A faixa que PINTA uma semana da tira. Semana sem carga fica neutra, como o quadrinho do
 * Mapa, e é por isso que ela passa por aqui e não direto por `faixaDaOcupacao`: a tira e o
 * resumo escrito ao lado dela contam a MESMA coisa, senão a legenda deixa de explicar as
 * cores.
 */
export function faixaDaSemanaDaVisao(semana: ISemanaDaVisao, faixas: IFaixasDeOcupacao): FaixaDeOcupacao {
  return faixaDaOcupacao(semana.semCarga ? null : semana.ocupacaoPct, faixas);
}

/** O bloco 4 de uma célula: quanto da carteira dela sai no prazo se nada mudar. */
export interface IPontualidadeDaVisao {
  /** OPs consideradas: as da célula, com prazo, que são costuradas aqui. */
  ops: number;
  opsNoPrazo: number;
  pctOps: number | null;
  valor: number;
  valorNoPrazo: number;
  pctValor: number | null;
}

/** Uma célula, com o que os blocos 2, 3 e 4 perguntam sobre ela. */
export interface ICelulaDaVisao {
  celula: string;
  pessoas: number;
  /** Ocupação da semana atual; `null` quando ninguém disse o tamanho da célula. */
  ocupacaoAtualPct: number | null;
  /** Semanas do período com folga: é nelas que ainda cabe carga nova. */
  semanasComFolga: number;
  semanasNoLimite: number;
  semanasNaoCabe: number;
  /** Semanas do período sem carga programada (ou sem capacidade registrada). */
  semanasSemCarga: number;
  semanas: Array<ISemanaDaVisao>;
  /** A partir de quando a célula fica sem carga; `null` sem nenhuma OP programada. */
  livreApartirDe: string | null;
  /**
   * A HORA daquele dia em que ela fica livre, quando a última OP acaba no meio do
   * expediente (`HH:MM`). `null` quando a célula fica livre no dia inteiro.
   */
  livreApartirDeHora: string | null;
  /** Horas livres das semanas com folga até `livreApartirDe`. */
  horasLivresAteLa: number;
  pontualidade: IPontualidadeDaVisao;
  /** O risco desta célula: a soma de todas fecha com o total (ver o spec). */
  riscoValor: number;
  riscoOps: number;
}

/** Uma marca do bloco 5, com a carteira dela e a parte em risco. */
export interface IMarcaDaVisao {
  marca: string;
  faturamento: number;
  ops: number;
  riscoValor: number;
  riscoOps: number;
  /** Quanto da carteira da marca está em risco; `null` quando ela não tem faturamento. */
  pctRisco: number | null;
  /** O maior atraso em dias úteis entre as OPs em risco dela, direto do motor. */
  maiorAtraso: number;
}

export interface IVisaoExecutiva {
  risco: IRiscoDaVisao;
  celulas: Array<ICelulaDaVisao>;
  marcas: Array<IMarcaDaVisao>;
  /**
   * O risco das OPs que não estão em nenhuma célula da grade (sem célula ou fora da
   * costura). Com ele a soma por célula fecha com o total, em vez de sumir uma fatia.
   */
  riscoSemCelula: number;
}

function somaFaturamento(ops: Array<EnrichedOp>): number {
  // Campo ausente nunca soma NaN: este número abre a tela que o diretor lê.
  return ops.reduce((total, op) => total + (op.faturamento ?? 0), 0);
}

/** Uma parte do total em pontos percentuais; `null` quando não há total para dividir. */
function percentual(parte: number, total: number): number | null {
  return total > 0 ? (parte / total) * PCT_CHEIO : null;
}

/** Quando a célula fica livre: o dia e, quando sobra expediente nele, a hora. */
export interface ILivreApartirDe {
  dia: string | null;
  /** `HH:MM` quando a célula fica livre no MEIO do dia; `null` quando é o dia inteiro. */
  hora: string | null;
}

/**
 * Quando a célula fica livre, pelo calendário da fábrica (jornada, fim de semana e
 * feriado ativo). É a partir daí que dá para vender capacidade nova sem mexer na
 * carteira de hoje.
 *
 * O motor programa em minutos corridos: a OP seguinte começa no minuto em que a
 * anterior termina. Com a HORA do fim na mão (`programacao.fimMinutoDoDia`, N45), a
 * célula que fecha a última OP às 14:30 fica livre às 14:30 do MESMO dia, e não no dia
 * útil seguinte: o que se jogava fora era o resto daquele dia.
 *
 * Acabando no fim do expediente (ou sem hora lida, numa jornada que não diz as horas),
 * a resposta volta a ser o próximo dia útil, que é o que ela sempre foi.
 *
 * Data ilegível, ou célula sem nenhuma OP programada, não vira um dia inventado:
 * devolve `null` nos dois campos.
 */
export function primeiroDiaLivreDaCelula(
  fim: string | null,
  fimMinutoDoDia: number | null,
  calendario: ICalendarioFabrica,
  jornada: Partial<IJornadaConfig> | undefined,
): ILivreApartirDe {
  if (fim === null || fim === '') {
    return { dia: null, hora: null };
  }
  const fimDoExpediente = fimDoExpedienteEmMinutos(diaDaSemanaDoISO(fim), jornada);
  const sobraExpediente =
    fimMinutoDoDia !== null && fimDoExpediente !== null && fimMinutoDoDia < fimDoExpediente;

  return sobraExpediente
    ? { dia: fim, hora: horaDoMinuto(fimMinutoDoDia) }
    : { dia: primeiroDiaUtilDepoisDe(fim, calendario), hora: null };
}

/** O dia da semana de uma data ISO, lido em UTC para o fuso de quem olha não mexer nele. */
function diaDaSemanaDoISO(iso: string): number {
  return new Date(`${iso}T00:00:00.000Z`).getUTCDay();
}

/** Quantas OPs vermelhas há em cada grau, pelos cortes em vigor. */
function gravidadeDaCarteira(
  ops: Array<EnrichedOp>,
  cortes: ICortesDaGravidade,
): Record<TGravidadeFarol, number> {
  const porGravidade: Record<TGravidadeFarol, number> = { grave: 0, medio: 0, leve: 0, semPrevisao: 0 };
  for (const op of ops) {
    // OP sem farol lido fica fora da conta em vez de derrubar a tela: esta linha abre o
    // bloco que o diretor olha primeiro.
    const cor = op.farol?.cor;
    const gravidade = cor === undefined ? null : gravidadeDoFarol(cor, atrasoDaOp(op), cortes);
    if (gravidade !== null) {
      porGravidade[gravidade] += 1;
    }
  }
  return porGravidade;
}

/** O bloco 1, sobre a carteira inteira. */
export function riscoDaVisao(
  ops: Array<EnrichedOp>,
  emRisco: Array<OpEmRisco>,
  cortes: ICortesDaGravidade,
  /** O dia da fábrica: é ele que separa a OP sem previsão da que já venceu. */
  hoje = '',
): IRiscoDaVisao {
  const faturamentoCarteira = somaFaturamento(ops);
  // A carteira inteira entra porque a OP SEM PREVISÃO não está em `emRisco`: ela não
  // tem data ao cliente para comparar com o prazo, e sumia do bloco inteiro.
  const resumo = resumoDoRisco(emRisco, ops, hoje);

  return {
    resumo,
    opsCarteira: ops.length,
    faturamentoCarteira,
    pctFaturamento: percentual(resumo.valor, faturamentoCarteira),
    porGravidade: gravidadeDaCarteira(ops, cortes),
  };
}

/**
 * O bloco 4 de um conjunto de OPs. PROJETADA: das OPs que têm prazo, quantas (e quanto em
 * reais) o motor entrega dentro dele, pela MESMA regra do risco (`riscoDaOp`).
 *
 * Fica de fora a OP sem prazo nenhum, porque não dá para mostrar que ela passou de um
 * prazo que não existe, e a OP fora da costura (costura externa, produto pronto), porque
 * nenhuma célula daqui decide a data dela.
 */
export function pontualidadeProjetada(ops: Array<EnrichedOp>, hoje: string): IPontualidadeDaVisao {
  const consideradas = ops.filter((op) => op.foraDaCostura === null && entregaDaOp(op) !== null);
  const noPrazo = consideradas.filter((op) => riscoDaOp(op, hoje) === null);
  const valor = somaFaturamento(consideradas);
  const valorNoPrazo = somaFaturamento(noPrazo);

  return {
    ops: consideradas.length,
    opsNoPrazo: noPrazo.length,
    pctOps: percentual(noPrazo.length, consideradas.length),
    valor,
    valorNoPrazo,
    pctValor: percentual(valorNoPrazo, valor),
  };
}

/** Os quadrinhos de uma célula, indexados pela semana que eles ocupam. */
function quadrosDaCelula(grade: GradeSemanas | null, celula: string): Map<string, GradeQuadro> {
  const porSemana = new Map<string, GradeQuadro>();
  for (const quadro of grade?.quadros ?? []) {
    if (quadro.celula === celula) {
      porSemana.set(quadro.semanaInicio, quadro);
    }
  }
  return porSemana;
}

/** O fim da ÚLTIMA OP que o motor programou numa célula; `null` quando não há nenhuma. */
function ultimoFimDaCelula(
  ops: Array<EnrichedOp>,
  celula: string,
): { dia: string | null; minutoDoDia: number | null } {
  return ops.reduce<{ dia: string | null; minutoDoDia: number | null }>(
    (maior, op) => {
      const fim = op.programacao.fim;
      if (op.celulaEfetiva !== celula || fim === null || fim === '') {
        return maior;
      }
      if (maior.dia !== null && fim < maior.dia) {
        return maior;
      }
      // Mesmo dia: vale a que termina MAIS TARDE, que é quando a célula de fato esvazia.
      const minutoDoDia = op.programacao.fimMinutoDoDia ?? null;
      if (fim === maior.dia && (maior.minutoDoDia ?? 0) >= (minutoDoDia ?? 0)) {
        return maior;
      }
      return { dia: fim, minutoDoDia };
    },
    { dia: null, minutoDoDia: null },
  );
}

/**
 * As horas que ainda dá para vender antes de a célula esvaziar: as horas livres das
 * semanas COM FOLGA antes do fim, mais o que sobra na PRÓPRIA semana em que a última OP
 * termina. A capacidade dos dias que já passaram não existe mais
 * (`horasLivresRestantes`).
 *
 * A semana do fim entra qualquer que seja a faixa dela: ela é a última semana com
 * carga, e a parte que sobra depois da OP é vendável mesmo numa semana apertada. As
 * anteriores só entram com folga, porque uma semana no limite ou estourada não tem nada
 * para oferecer.
 *
 * Célula sem data livre não tem nenhum "até lá": somar as semanas do horizonte diria que
 * existe uma espera que não existe.
 */
function horasLivresAte(
  quadros: Map<string, GradeQuadro>,
  fimDaUltimaOp: string | null,
  livreApartirDe: string | null,
  faixas: IFaixasDeOcupacao,
): number {
  if (livreApartirDe === null || fimDaUltimaOp === null) {
    return 0;
  }
  const semanaDoFim = inicioDaSemana(fimDaUltimaOp);
  let horas = 0;
  for (const [semanaInicio, quadro] of quadros) {
    if (semanaInicio > semanaDoFim) {
      continue;
    }
    const ehSemanaDoFim = semanaInicio === semanaDoFim;
    if (ehSemanaDoFim || faixaDaOcupacao(quadro.ocupacaoPct, faixas) === 'folga') {
      horas += horasLivresRestantes(quadro);
    }
  }
  return horas;
}

interface ICelulasDaVisaoParams {
  grade: GradeSemanas | null;
  ops: Array<EnrichedOp>;
  emRisco: Array<OpEmRisco>;
  hoje: string;
  faixas: IFaixasDeOcupacao;
  /** O calendário da fábrica: é ele que diz qual é o próximo dia útil. */
  calendario: ICalendarioFabrica;
  /**
   * As semanas do PERÍODO escolhido. A tira e as contagens de "no limite" e "não cabe"
   * falam só delas; a data livre e as horas livres continuam lendo a grade inteira,
   * porque "quando a célula esvazia" não é uma pergunta sobre o recorte.
   */
  semanasVisiveis: Array<GradeSemana>;
  /** A jornada em vigor: é ela que diz quando o expediente do dia do fim acaba. */
  jornada: Partial<IJornadaConfig> | undefined;
}

/**
 * Uma linha por célula de costura, na ordem da grade do Mapa: são as células do cálculo
 * de prazo, e não as que a OP por acaso carrega.
 */
export function celulasDaVisao({
  grade,
  ops,
  emRisco,
  hoje,
  faixas,
  calendario,
  semanasVisiveis,
  jornada,
}: ICelulasDaVisaoParams): Array<ICelulaDaVisao> {
  const semanas = semanasVisiveis;

  return (grade?.celulas ?? []).map((celula) => {
    const quadros = quadrosDaCelula(grade, celula);
    const daCelula = ops.filter((op) => op.celulaEfetiva === celula);
    const riscoDaCelula = emRisco.filter((item) => item.op.celulaEfetiva === celula);
    const ultimoFim = ultimoFimDaCelula(ops, celula);
    const fimDaUltimaOp = ultimoFim.dia;
    const livre = primeiroDiaLivreDaCelula(
      fimDaUltimaOp,
      ultimoFim.minutoDoDia,
      calendario,
      jornada,
    );

    const leituras: Array<ISemanaDaVisao> = semanas.map((semana) => {
      const quadro = quadros.get(semana.inicio);
      return {
        inicio: semana.inicio,
        atual: semana.atual,
        ocupacaoPct: quadro?.ocupacaoPct ?? null,
        semCarga: quadro === undefined || quadro.ocupacaoPct === null || quadro.horasProgramadas <= 0,
      };
    });
    /** Quantas semanas do período caem em cada cor, pela mesma faixa que pinta a tira. */
    const quantasNaFaixa = (faixa: FaixaDeOcupacao): number =>
      leituras.filter((semana) => faixaDaSemanaDaVisao(semana, faixas) === faixa).length;

    return {
      celula,
      pessoas: grade?.pessoasPorCelula?.[celula] ?? 0,
      ocupacaoAtualPct: leituras.find((semana) => semana.atual)?.ocupacaoPct ?? null,
      semanasComFolga: quantasNaFaixa('folga'),
      semanasNoLimite: quantasNaFaixa('limite'),
      semanasNaoCabe: quantasNaFaixa('estouro'),
      semanasSemCarga: quantasNaFaixa('vazio'),
      semanas: leituras,
      livreApartirDe: livre.dia,
      livreApartirDeHora: livre.hora,
      horasLivresAteLa: horasLivresAte(quadros, fimDaUltimaOp, livre.dia, faixas),
      pontualidade: pontualidadeProjetada(daCelula, hoje),
      riscoValor: riscoDaCelula.reduce((total, item) => total + item.valor, 0),
      riscoOps: riscoDaCelula.length,
    };
  });
}

/**
 * O bloco 5, por MARCA e pela maior carteira primeiro: é por marca que a confecção é
 * cobrada, e o faturamento em carteira é o que decide quem olhar antes.
 *
 * `maiorAtraso` vem de `op.diasAtraso`, o número que o MOTOR gravou e que a coluna "Dias
 * de atraso" da Carteira e a Fila mostram: a tela não recalcula atraso.
 */
export function marcasDaVisao(ops: Array<EnrichedOp>, emRisco: Array<OpEmRisco>): Array<IMarcaDaVisao> {
  const porMarca = new Map<string, IMarcaDaVisao>();

  const daMarca = (marca: string): IMarcaDaVisao =>
    porMarca.get(marca) ?? {
      marca,
      faturamento: 0,
      ops: 0,
      riscoValor: 0,
      riscoOps: 0,
      pctRisco: null,
      maiorAtraso: 0,
    };

  for (const op of ops) {
    const marca = (op.marca ?? '').trim();
    const atual = daMarca(marca);
    atual.faturamento += op.faturamento ?? 0;
    atual.ops += 1;
    porMarca.set(marca, atual);
  }

  for (const item of emRisco) {
    const marca = (item.op.marca ?? '').trim();
    const atual = daMarca(marca);
    atual.riscoValor += item.valor;
    atual.riscoOps += 1;
    atual.maiorAtraso = Math.max(atual.maiorAtraso, item.op.diasAtraso ?? 0);
    porMarca.set(marca, atual);
  }

  return [...porMarca.values()]
    .map((marca) => ({ ...marca, pctRisco: percentual(marca.riscoValor, marca.faturamento) }))
    .sort((a, b) => b.faturamento - a.faturamento || a.marca.localeCompare(b.marca, 'pt-BR'));
}

interface IVisaoExecutivaParams {
  /** A carteira INTEIRA: os cinco blocos falam dela, nunca de uma página dela. */
  ops: Array<EnrichedOp>;
  /** As OPs em risco, já lidas pelo helper da Carteira (`opsEmRisco`). */
  emRisco: Array<OpEmRisco>;
  grade: GradeSemanas | null;
  hoje: string;
  faixas: IFaixasDeOcupacao;
  /** O calendário da fábrica: é ele que diz qual é o próximo dia útil. */
  calendario: ICalendarioFabrica;
  /** As semanas do período escolhido: só o bloco da capacidade se recorta por elas. */
  semanasVisiveis: Array<GradeSemana>;
  /** Os cortes que graduam o vermelho (Configurações › Prazo). */
  cortes: ICortesDaGravidade;
  /** A jornada em vigor: é ela que diz quando o expediente do dia do fim acaba. */
  jornada: Partial<IJornadaConfig> | undefined;
}

/** A tela inteira numa leitura só, para os cinco blocos nunca discordarem entre si. */
export function visaoExecutiva({
  ops,
  emRisco,
  grade,
  hoje,
  faixas,
  calendario,
  semanasVisiveis,
  cortes,
  jornada,
}: IVisaoExecutivaParams): IVisaoExecutiva {
  const celulas = celulasDaVisao({
    grade,
    ops,
    emRisco,
    hoje,
    faixas,
    calendario,
    semanasVisiveis,
    jornada,
  });
  const risco = riscoDaVisao(ops, emRisco, cortes, hoje);
  const riscoNasCelulas = celulas.reduce((total, celula) => total + celula.riscoValor, 0);

  return {
    risco,
    celulas,
    marcas: marcasDaVisao(ops, emRisco),
    riscoSemCelula: risco.resumo.valor - riscoNasCelulas,
  };
}
